import _ from "lodash";
import { ChargeDetailConstant } from "presentation/constant/ChargeDetailMaintenance/ChargeDetailConstant";
import { adjIndOptions } from "presentation/constant/RadioOptions/StaticRadioOptions";
import { WorkspaceConstant } from "presentation/constant/WorkSpaceConstant";
import { useChargeDetailVM } from "presentation/hook/ChargeDetailMaintenance/useChargeDetailVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { useChargeDetailTracked } from "presentation/store/ChargeDetailMaintenance/ChargeDetailProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import { NbisCommonField } from "presentation/view/components/NbisCommonField";
import { memo, useCallback, useMemo, useState } from "react";
import { DialogModal, FieldType, GroupCheckboxList, HPHButton, HPHGroupCheckbox, HPHGroupRadioButton, IconButton, IFieldValue, Loader } from "veronica-ui-component/dist/component/core";

const ChargeDetailPrintModal: React.FC = () => {
    const chargeDetailVM = useChargeDetailVM();
    const [chargeDetailState] = useChargeDetailTracked();
    const messageBarVM = useMessageBarVM();

    const CHG_CONSTANT = ChargeDetailConstant.Print;
    const { isShowPrintModal, printCriteria, dynamicOptions, chargeTypeCheckboxOptions, checkListIdMap, currentChargeHeader } = chargeDetailState;
    const { printerDropdownOptions, } = dynamicOptions;
    const [isLoading, setIsLoading] = useState(false);

    const handleReprint = useCallback(async () => {
        if (!(printCriteria?.printer)) {
            messageBarVM.showError("Please select a Printer.", true);
            return;
        }
        if (_.isEmpty(printCriteria.rptList)) {
            messageBarVM.showError("Please select a report to print.", true);
            return;
        }
        setIsLoading(true);
        const chargeTypeList = printCriteria.rptList;
        const newRptList = chargeTypeList?.filter(key => key in checkListIdMap).map(key => checkListIdMap[key] ?? "".toString());
        let newPrintCriteria = printCriteria;
        newPrintCriteria = {
            ...newPrintCriteria,
            rptList: newRptList,
            isPrint: true,
            chargeHdrId: currentChargeHeader?.id
        }
        chargeDetailVM.onReprint(newPrintCriteria).then((data) => {
            if (data && data["printReportFail"]) {
                messageBarVM.showError(data["printReportFail"], true);
                return;
            }

            messageBarVM.showSuccess("Report is sent to printers successfully.", true);
        }).catch((error) => {
            setIsLoading(false);
            messageBarVM.showError(error.message)
        }).finally(() => {
            setIsLoading(false);
        });
    }, [printCriteria, currentChargeHeader?.id, chargeDetailVM, messageBarVM, checkListIdMap]);

    const handlePreview = useCallback(async () => {
        if (!(printCriteria?.printer)) {
            messageBarVM.showError("Please select a Printer.", true);
            return;
        }

        if (_.isEmpty(printCriteria.rptList)) {
            messageBarVM.showError("Please select a report to preview.", true);
            return;
        }
        setIsLoading(true);
        const chargeTypeList = printCriteria.rptList;
        const newRptList = chargeTypeList?.filter(key => key in checkListIdMap).map(key => checkListIdMap[key] ?? "".toString());
        let newPrintCriteria = printCriteria;
        newPrintCriteria = {
            ...newPrintCriteria,
            rptList: newRptList,
            isPrint: false,
            chargeHdrId: currentChargeHeader?.id
        }
        chargeDetailVM.onPreview(newPrintCriteria).then((data) => {
            if (data && data["printReportFail"]) {
                messageBarVM.showError(data["printReportFail"], true);
                return;
            }

            messageBarVM.showSuccess("Preview report successfully.", true);
        }).catch((error) => {
            setIsLoading(false);
            messageBarVM.showError(error.message)
        }).finally(() => {
            setIsLoading(false);
        });
    }, [printCriteria, currentChargeHeader?.id, chargeDetailVM, messageBarVM, checkListIdMap]);

    const memoAdjInd = useMemo(() =>
        <div className='im-flex-row-item' style={{ minWidth: "250px", maxWidth: "250px", width: "47.5%", marginBottom: "24px" }}>
            <HPHGroupRadioButton
                label="CheckList"
                radioOptions={adjIndOptions}
                value={printCriteria.radAdjustment ?? ''}
                onChange={(e) => chargeDetailVM.onRadioChange(e, 'radAdjustment')}
                checked={adjIndOptions?.find(item => item.key === printCriteria.radAdjustment)?.name}
            />
        </div>
        , [chargeDetailVM, printCriteria.radAdjustment])

    const memoChargeTypeCheckBox = useMemo(() =>
        <div className='im-flex-row-item' style={{ minWidth: "250px", maxWidth: "530px", width: "100%", marginBottom: "15px" }}>
            <HPHGroupCheckbox
                label={CHG_CONSTANT.CHARGE_TYPE}
                checkboxData={chargeTypeCheckboxOptions}
                selectedValues={printCriteria.rptList}
                orientation={"vertical"}
                onChange={(e: (GroupCheckboxList | undefined)[]) => chargeDetailVM.onGroupCheckboxChange(e)}
            />
        </div>
        , [CHG_CONSTANT.CHARGE_TYPE, chargeDetailVM, chargeTypeCheckboxOptions, printCriteria.rptList])

    const memoPrinter = useMemo(() =>
        <div className='im-flex-row-item' style={{ minWidth: "250px", maxWidth: "250px", width: "47.5%", marginBottom: "24px" }}>
            <NbisCommonField
                //errorMessages={allFormState}
                isReadOnly={false}
                isShowOptional={false}
                isMultipleDropDown={false}
                readOnlyValue={printCriteria.printer ?? ""}
                fieldValue={printCriteria.printer ?? ""}
                fieldLabel={CHG_CONSTANT.PRINTER}
                //isSaveClicked={isSaveClicked}
                fieldType={FieldType.DROPDOWN}
                fieldKey={'printer'}
                isShowMissingError={true}
                maxLength={10}
                options={printerDropdownOptions}
                requiredFieldList={[]}
                onFieldChange={(fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) =>
                    chargeDetailVM.onPrintCriteriaFieldChange(fieldKey, fieldValue && fieldValue.toString(), fFullValue)} />
        </div>
        , [CHG_CONSTANT.PRINTER, chargeDetailVM, printCriteria.printer, printerDropdownOptions])

    const refreshModal = () => {
        if (document) {
            const myModal = document.getElementById("chargeDetailPrintModal");
            if (myModal) {
                const myContent = document.querySelector(".p-dialog-content");
                if (myContent) {
                    (myContent as HTMLElement).style.overflow = "visible";
                }
            }
        }
    }

    if (isLoading) return <Loader Indicator="Stripe" size="Large" />;

    return <DialogModal style={{ minWidth: '35vw', minHeight: '60vh' }}
        appendTo='self'
        showButton={false}
        visible={isShowPrintModal}
        positions="center"
        resizable={false}
        header={
            <div className="im-charge-data-search-confirm-modal-header">
                <span className="name">{CHG_CONSTANT.TITLE}</span>
                <IconButton fileName='Icon-cross' size="medium" onClick={chargeDetailVM.onClosePrintModal} tooltipDisable={true} />
            </div>
        }
        dialogContent={
            // <div className={"im-charge-data-search-confirm-modal-content"}>
            <div className={'add-edit-form'} style={{ maxHeight: '40vh', height: '40vh', overflow: 'auto' }}>
                <div className="flex-row-auto">
                    <div className={'flex-row-item'}>
                        <CriteriaItemContainer>
                            {memoAdjInd}
                            {memoPrinter}
                        </CriteriaItemContainer>

                        <CriteriaItemContainer>
                            {memoChargeTypeCheckBox}
                        </CriteriaItemContainer>

                    </div>
                </div>
            </div>
        }
        className="im-charge-data-search-confirm-modal"
        id="chargeDetailPrintModal"
        onShow={refreshModal}
        footer={
            <div className="im-charge-data-search-confirm-modal-footer">
                <HPHButton label={WorkspaceConstant.Common.BUTTON_CANCEL} size={'Small'} theme={'Secondary'} onClick={chargeDetailVM.onClosePrintModal} />
                <HPHButton label={"Preview"} size={'Small'} theme={'Secondary'} onClick={handlePreview} />
                <HPHButton label={WorkspaceConstant.Common.BUTTON_PRINT} size={'Small'} theme={'Primary'} onClick={handleReprint} />
            </div>
        }
    />;
};

export default memo(ChargeDetailPrintModal);